import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import BlockedIpsTable from './components/BlockedIpsTable';
import useAdminBlockedIps from './hooks/useAdminBlockedIps';
import { UnblockAdminIpAPI } from '../../../api/AdminAPI';

/**
 * AdminBlockedIps - IPs bloqueados por tentativas excessivas ou limite de requisições (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminBlockedIps() para carregar a lista completa (sem filtros/paginação)
 * - Renderiza AreaTitle + contadores + botão "Atualizar" + tabela
 * - Orquestra a ação "Liberar" (ConfirmDialog -> UnblockAdminIpAPI -> Toast + refresh)
 */
function AdminBlockedIps() {
  const { blockedIps, total, currentlyBlocked, isLoading, error, refresh } = useAdminBlockedIps();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [ipToUnblock, setIpToUnblock] = useState(null);
  const [unblocking, setUnblocking] = useState(false);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleUnblockRequest = (entry) => {
    setIpToUnblock(entry);
  };

  const handleUnblockCancel = () => {
    if (unblocking) return;
    setIpToUnblock(null);
  };

  const handleUnblockConfirm = () => {
    if (!ipToUnblock) return;
    setUnblocking(true);
    UnblockAdminIpAPI(ipToUnblock.ip, (response) => {
      setUnblocking(false);
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao liberar o IP. Tente novamente.', 'error');
        return;
      }
      setIpToUnblock(null);
      showToast('IP liberado com sucesso.', 'success');
      refresh();
    });
  };

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · IPs bloqueados"
          Icon={<BlockOutlinedIcon />}
          Description="Acompanhe e libere IPs bloqueados por tentativas excessivas de login ou limite de requisições."
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Bloqueados agora: <strong style={{ color: '#C62828' }}>{currentlyBlocked}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total de registros: <strong>{total}</strong>
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={refresh}
          disabled={isLoading}
        >
          Atualizar
        </Button>
      </Box>

      <BlockedIpsTable
        blockedIps={blockedIps}
        isLoading={isLoading}
        error={error}
        onUnblock={handleUnblockRequest}
      />

      <ConfirmDialog
        isOpen={Boolean(ipToUnblock)}
        title="Liberar IP bloqueado"
        message={ipToUnblock ? `Liberar o IP ${ipToUnblock.ip}? Os contadores de tentativas serão zerados.` : ''}
        variant="info"
        confirmLabel="Liberar"
        cancelLabel="Cancelar"
        onConfirm={handleUnblockConfirm}
        onCancel={handleUnblockCancel}
        loading={unblocking}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminBlockedIps;
